import { Args, AxiosConfig, AxiosPagePromise, BaseService } from './BaseService';
import { AxiosPromise } from 'axios';
import { Resource, ExportResource } from '../resources';
import { Page } from '../models/common/Page';

export interface CrudResources {
  /**
   * The parameter name used for the entity ID in API calls.
   * This corresponds to the path parameter name in the resource URL.
   * @example 'workplaceId' for /workplaces/{workplaceId}
   * @example 'id' for /entry-request/{id}
   * @example 'carId' for /suppliers/cars/{carId}
   */
  idKey: string;
  query?: Resource;
  get?: Resource;
  create?: Resource;
  update?: Resource;
  delete?: Resource;
}

/**
 * Generic CRUD service that provides standard create, read, update, delete operations.
 * Services can extend this class to get CRUD functionality out of the box.
 *
 * @template TDto - The DTO type for this entity
 * @template TQueryParams - The query parameters type (defaults to Args)
 * @template U - The DTO for create/update
 *
 * @example
 * ```typescript
 * export class WorkplaceService extends CrudService<WorkplaceDto, WorkplaceQueryParams> {
 *   constructor() {
 *     super({
 *       query: resources.queryWorkplace,
 *       get: resources.getWorkplace,
 *       create: resources.createWorkplace,
 *       update: resources.updateWorkplace,
 *       idKey: 'workplaceId'
 *     });
 *   }
 *
 *   // Add custom methods here
 *   public async getWorkplaceNotificationEmployees(workplaceIds: string[]): AxiosPromise<EmployeeDto[]> {
 *     return this.get<EmployeeDto[]>(resources.getWorkplaceNotificationEmployees, { workplaceIds });
 *   }
 * }
 * ```
 */
export class CrudService<TDto, TQueryParams = Args, U = TDto> extends BaseService {
  constructor(protected res: CrudResources) {
    super();
  }

  public query(args?: TQueryParams, options: AxiosConfig = {}): AxiosPagePromise<TDto> {
    if (!this.res.query) throw new Error('Query resource not defined for this service');
    return this.get<Page<TDto>>(this.res.query, args as Args, options);
  }

  public getById(id: string, args?: Args, options: AxiosConfig = {}): AxiosPromise<TDto> {
    if (!this.res.get) throw new Error('Get resource not defined for this service');
    return this.get<TDto>(this.res.get, { ...args, [this.res.idKey]: id }, options);
  }

  public create(dto: U, args?: Args, options: AxiosConfig = {}): AxiosPromise<TDto> {
    if (!this.res.create) throw new Error('Create resource not defined for this service');
    return this.post<TDto>(this.res.create, dto, args, options);
  }

  public update(id: string, dto: U, args?: Args, options: AxiosConfig = {}): AxiosPagePromise<TDto> {
    if (!this.res.update) throw new Error('Update resource not defined for this service');
    return this.put<Page<TDto>>(this.res.update, dto, { ...args, [this.res.idKey]: id }, options);
  }

  public async deleteById(id: string, args?: Args, options: AxiosConfig = {}): Promise<boolean> {
    if (!this.res.delete) throw new Error('Delete resource not defined for this service');
    const response = await this.delete<void>(this.res.delete, { ...args, [this.res.idKey]: id }, options);
    return response.status !== 0;
  }

  public export(args?: TQueryParams, options: AxiosConfig = {}): AxiosPromise<Blob> {
    if (!this.res.query) throw new Error('Query resource not defined for this service');

    const exportOverrides: ExportResource = this.res.query.exportResource ?? {};
    const exportResource: Resource = {
      ...this.res.query,
      path: this.res.query.path + '/export',
      getSuccessMessageKey: exportOverrides.getSuccessMessageKey ?? (() => 'common.exportSuccess'),
      getErrorMessageKey: exportOverrides.getErrorMessageKey ?? this.res.query.getErrorMessageKey,
    };

    return this.get<Blob>(exportResource, args as Args, { responseType: 'blob', ...options });
  }
}
